import React, { useEffect, useMemo, useState } from 'react';
import { Select, InputNumber, Space, Radio } from 'antd';
import { useTranslation } from '../../i18n';

/**
 * 预设 + 自定义(数值 + 单位)输入。值始终以字段原本的基础单位存储(秒 / 毫秒 / 字节 / MB / 次),
 * 只是展示成人能读懂的「2 小时」「10 MB」。
 *
 *   <DurationInput presets={[60, 3600]} min={0} max={31536000} zeroLabel={t('No cache')} />
 *   <SizeInput base="B" presets={[1024]} />
 *   <CountInput presets={[10, 100]} zeroLabel={t('Unlimited')} />
 */

const CUSTOM = '__custom__';
const EMPTY = '__empty__';

const SEC_UNITS = [
  { key: 's', factor: 1, label: 'sec', fmt: '{{n}} s' },
  { key: 'min', factor: 60, label: 'min', fmt: '{{n}} min' },
  { key: 'h', factor: 3600, label: 'hour', fmt: '{{n}} h' },
  { key: 'd', factor: 86400, label: 'day', fmt: '{{n}} d' },
];
const MS_UNITS = [
  { key: 'ms', factor: 1, label: 'ms', fmt: '{{n}} ms' },
  { key: 's', factor: 1000, label: 'sec', fmt: '{{n}} s' },
  { key: 'min', factor: 60000, label: 'min', fmt: '{{n}} min' },
];
const KB = 1024;
const BYTE_UNITS = [
  { key: 'B', factor: 1, label: 'B', fmt: '{{n}} B' },
  { key: 'KB', factor: KB, label: 'KB', fmt: '{{n}} KB' },
  { key: 'MB', factor: KB * KB, label: 'MB', fmt: '{{n}} MB' },
  { key: 'GB', factor: KB * KB * KB, label: 'GB', fmt: '{{n}} GB' },
];
const MB_UNITS = [
  { key: 'MB', factor: 1, label: 'MB', fmt: '{{n}} MB' },
  { key: 'GB', factor: KB, label: 'GB', fmt: '{{n}} GB' },
];

const YEAR = 31536000;

// 选能整除的最大单位(展示 7200 → 2 h)
const bestUnit = (v, units) => {
  if (!v) return units[0];
  for (let i = units.length - 1; i >= 0; i--) {
    if (v % units[i].factor === 0) return units[i];
  }
  return units[0];
};

const fmtWith = (v, units, t) => {
  const u = bestUnit(v, units);
  return t(u.fmt, { n: v / u.factor });
};

/** 7200 → "2 h"(按当前语言) */
export const formatDuration = (sec, t) => {
  if (sec && sec % YEAR === 0) return t('{{n}} yr', { n: sec / YEAR });
  return fmtWith(Number(sec) || 0, SEC_UNITS, t);
};
export const formatSize = (bytes, t) => fmtWith(Number(bytes) || 0, BYTE_UNITS, t);

const isEmptyVal = (v) => v === null || v === undefined || v === '';

/** 核心:预设下拉 + 自定义(数值 + 单位) */
const PresetUnitInput = ({
  value, onChange, presets = [], units, min = 0, max, zeroLabel, emptyLabel, format,
  disabled, size, style, suffix, placeholder,
}) => {
  const { t } = useTranslation();
  const presetVals = useMemo(() => presets.map(Number), [presets]);
  const inPresets = (v) => presetVals.includes(Number(v)) || (Number(v) === 0 && zeroLabel && !isEmptyVal(v));
  const [custom, setCustom] = useState(() => !isEmptyVal(value) && !inPresets(value));
  const [unit, setUnit] = useState(() => bestUnit(Number(value) || 0, units).key);

  // 外部值变化(加载数据)时同步模式与单位
  useEffect(() => {
    if (!isEmptyVal(value) && !inPresets(value)) setCustom(true);
    if (!isEmptyVal(value) && Number(value) > 0) {
      const cur = units.find((u) => u.key === unit);
      if (!cur || Number(value) % cur.factor !== 0) setUnit(bestUnit(Number(value), units).key);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const clamp = (v) => {
    let n = Math.round(Number(v) || 0);
    if (n < min) n = min;
    if (max !== undefined && n > max) n = max;
    return n;
  };

  const label = (v) => {
    const s = format ? format(v) : fmtWith(v, units, t);
    return suffix ? `${s}${suffix}` : s;
  };

  const options = [
    ...(emptyLabel ? [{ value: EMPTY, label: emptyLabel }] : []),
    ...(zeroLabel && !presetVals.includes(0) ? [{ value: '0', label: zeroLabel }] : []),
    ...presetVals.map((p) => ({ value: String(p), label: p === 0 && zeroLabel ? zeroLabel : label(p) })),
    { value: CUSTOM, label: t('Custom') },
  ];

  const selectVal = custom ? CUSTOM : (isEmptyVal(value) ? (emptyLabel ? EMPTY : undefined) : String(Number(value)));

  const onSelect = (v) => {
    if (v === CUSTOM) {
      setCustom(true);
      return;
    }
    setCustom(false);
    if (v === EMPTY) onChange?.(null);
    else onChange?.(Number(v));
  };

  const visibleUnits = units.filter((u) => max === undefined || u.factor <= max);
  const cur = visibleUnits.find((u) => u.key === unit) || visibleUnits[0];
  const shown = isEmptyVal(value) ? null : Number(value) / cur.factor;

  return (
    <Space.Compact style={{ width: '100%', ...style }} size={size}>
      <Select
        value={selectVal}
        onChange={onSelect}
        options={options}
        disabled={disabled}
        placeholder={placeholder}
        style={{ width: custom ? '42%' : '100%', minWidth: 96 }}
        popupMatchSelectWidth={false}
      />
      {custom && (
        <InputNumber
          value={shown}
          min={Math.ceil(min / cur.factor)}
          max={max !== undefined ? Math.floor(max / cur.factor) : undefined}
          precision={cur.factor === 1 ? 0 : 2}
          disabled={disabled}
          onChange={(n) => onChange?.(n === null ? (emptyLabel ? null : min) : clamp(n * cur.factor))}
          style={{ flex: 1, width: units.length > 1 ? '33%' : '58%' }}
        />
      )}
      {custom && visibleUnits.length > 1 && (
        <Select
          value={cur.key}
          disabled={disabled}
          onChange={(k) => {
            const nu = visibleUnits.find((u) => u.key === k);
            setUnit(k);
            if (!isEmptyVal(value)) onChange?.(clamp(shown * nu.factor));
          }}
          options={visibleUnits.map((u) => ({ value: u.key, label: t(u.label) }))}
          style={{ width: '25%', minWidth: 72 }}
          popupMatchSelectWidth={false}
        />
      )}
    </Space.Compact>
  );
};

/** 时长:base='s'(默认) 或 'ms'。 */
export const DurationInput = ({ base = 's', ...props }) => {
  const { t } = useTranslation();
  const units = base === 'ms' ? MS_UNITS : SEC_UNITS;
  const format = base === 'ms' ? undefined : (v) => formatDuration(v, t);
  return <PresetUnitInput units={units} format={format} {...props} />;
};

/** 大小:base='B'(字节,默认) 或 'MB'。 */
export const SizeInput = ({ base = 'B', ...props }) => (
  <PresetUnitInput units={base === 'MB' ? MB_UNITS : BYTE_UNITS} {...props} />
);

/** 计数:预设 + 自定义数字 */
const COUNT_UNITS = [{ key: 'n', factor: 1, label: '', fmt: '{{n}}' }];
export const CountInput = (props) => {
  const fmt = (v) => Number(v).toLocaleString();
  return <PresetUnitInput units={COUNT_UNITS} format={fmt} {...props} />;
};

/** 限速档位:宽松 / 标准 / 严格(同时设定次数 + 窗口) */
export const RATE_PRESETS = [
  { key: 'loose', label: 'Lenient', requests: 300, ttl: 60 },
  { key: 'standard', label: 'Standard', requests: 100, ttl: 60 },
  { key: 'strict', label: 'Strict', requests: 20, ttl: 60 },
];
export const RateLimitPresets = ({ requests, ttl, onPick, size = 'small' }) => {
  const { t } = useTranslation();
  const cur = RATE_PRESETS.find((p) => p.requests === Number(requests) && p.ttl === Number(ttl));
  return (
    <Radio.Group
      size={size}
      optionType="button"
      value={cur?.key}
      onChange={(e) => {
        const p = RATE_PRESETS.find((x) => x.key === e.target.value);
        if (p) onPick({ requests: p.requests, ttl: p.ttl });
      }}
      options={RATE_PRESETS.map((p) => ({
        value: p.key,
        label: `${t(p.label)} · ${t('{{n}}/min', { n: p.requests })}`,
      }))}
    />
  );
};

// 常用预设(秒)
export const TTL_PRESETS = [60, 600, 3600, 86400, 604800, 2592000, YEAR];

export default DurationInput;
